import {
  BookOpen,
  Bot,
  Briefcase,
  Headphones,
  LifeBuoy,
  MessagesSquare,
  Shield,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import type {
  OmniChatFormState,
  OmniChatIconId,
  OmniChatPolicies,
  OmniChatPolicyFlag,
  OmniChatPolicyId,
  OmniChatPolicyMode,
} from './omniChatTypes';

export const OMNICHAT_PAGE_ID = 'a9c3e7b1-4f28-4d6a-9e15-7c2b8f0d4a61';
export const OMNICHAT_LISTING_ID = 'b1d4f8a2-6c39-4e7b-8a15-3d9e7c2f5b80';
export const OMNICHAT_DETAIL_ID = 'c2e5a9b3-7d4a-4f8c-9b26-4e0f8d3a6c91';
export const OMNICHAT_SECURITY_MODULE_ID = 'e4b7c2d8-1a5f-4e9c-8d3b-6f0a2c7e9b14';
export const OMNICHAT_PAGE_IDS = [OMNICHAT_PAGE_ID, OMNICHAT_LISTING_ID, OMNICHAT_DETAIL_ID] as const;

export const OMNICHAT_BASE_PATH = '/portal/omnichat';
export const OMNICHAT_PATHS = {
  list: OMNICHAT_BASE_PATH,
  create: `${OMNICHAT_BASE_PATH}/new`,
  detail: (id: string) => `${OMNICHAT_BASE_PATH}/${id}`,
} as const;

export function isOmniChatPageId(id: string): boolean {
  return (OMNICHAT_PAGE_IDS as readonly string[]).includes(id);
}

export function isOmniChatChildId(id: string): boolean {
  return id === OMNICHAT_LISTING_ID || id === OMNICHAT_DETAIL_ID;
}

export function liveChatUrl(id: string, baseUrl = ''): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  const base = (baseUrl || origin).replace(/\/+$/, '');
  return `${base}/chat/omnichat/${id}`;
}

export const OMNICHAT_ICON_OPTIONS: { id: OmniChatIconId; icon: LucideIcon; label: string }[] = [
  { id: 'messages', icon: MessagesSquare, label: 'Chat' },
  { id: 'bot', icon: Bot, label: 'Agent' },
  { id: 'sparkles', icon: Sparkles, label: 'Assist' },
  { id: 'headset', icon: Headphones, label: 'Support' },
  { id: 'lifebuoy', icon: LifeBuoy, label: 'Help' },
  { id: 'briefcase', icon: Briefcase, label: 'Work' },
  { id: 'book', icon: BookOpen, label: 'Knowledge' },
  { id: 'shield', icon: Shield, label: 'Trust' },
];

export function omniChatIcon(id: OmniChatIconId): LucideIcon {
  return OMNICHAT_ICON_OPTIONS.find((option) => option.id === id)?.icon ?? MessagesSquare;
}

const POLICY_IDS: OmniChatPolicyId[] = [
  'grounding',
  'promptInjection',
  'pii',
  'prohibitedContent',
  'topicFence',
  'confirmWrites',
];

export function defaultPolicyFlag(): OmniChatPolicyFlag {
  return { enabled: true, mode: 'enforce' };
}

export function defaultPolicies(): OmniChatPolicies {
  return {
    grounding: defaultPolicyFlag(),
    promptInjection: defaultPolicyFlag(),
    pii: defaultPolicyFlag(),
    prohibitedContent: defaultPolicyFlag(),
    topicFence: defaultPolicyFlag(),
    confirmWrites: defaultPolicyFlag(),
  };
}

function isPolicyMode(value: unknown): value is OmniChatPolicyMode {
  return value === 'monitor' || value === 'enforce';
}

export function coercePolicies(value?: Partial<OmniChatPolicies> | null): OmniChatPolicies {
  const next = defaultPolicies();
  if (!value) return next;
  POLICY_IDS.forEach((id) => {
    const flag = value[id];
    if (!flag) return;
    next[id] = {
      enabled: flag.enabled !== false,
      mode: isPolicyMode(flag.mode) ? flag.mode : 'enforce',
    };
  });
  return next;
}

export function emptyOmniChatForm(gatewayId = ''): OmniChatFormState {
  return {
    title: '',
    description: '',
    iconId: 'messages',
    instruction: '',
    gatewayId,
    inferenceQuotaId: '',
    connectorIds: [],
    knowledgeBaseIds: [],
    policies: defaultPolicies(),
  };
}

export function formFromAgent(agent: {
  title: string;
  description: string;
  iconId: OmniChatIconId;
  instruction: string;
  gatewayId: string;
  inferenceQuotaId?: string;
  connectorIds: OmniChatFormState['connectorIds'];
  knowledgeBaseIds?: string[];
  policies?: Partial<OmniChatPolicies> | null;
}): OmniChatFormState {
  return {
    title: agent.title,
    description: agent.description,
    iconId: agent.iconId,
    instruction: agent.instruction,
    gatewayId: agent.gatewayId,
    inferenceQuotaId: agent.inferenceQuotaId || '',
    connectorIds: [...agent.connectorIds],
    knowledgeBaseIds: [...(agent.knowledgeBaseIds ?? [])],
    policies: coercePolicies(agent.policies),
  };
}
